import React, { useState, useEffect, useRef } from 'react';

import { useTheme } from '@mui/material/styles';
import { Box, Typography, TextField, } from "@mui/material";

const IpInputField = ({ value, onChange, disabled = false, error = false }) => {
    const theme = useTheme();
    const [ip, setIp] = useState(["", "", "", ""]);
    const inputRefs = useRef([]);
    const boxRef = useRef(null);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (value) {
            const formatValue = value.split(".");
            if (formatValue.length === 4 && formatValue.every((item) => /^\d{1,3}$/.test(item))) {
                setIp(formatValue);
            }
        }
    }, [value]);

    function handleChange(value, index) {
        if (/^\d{0,3}$/.test(value)) {
            const newIp = [...ip];
            newIp[index] = value;
            setIp(newIp);

            const allEmpty = newIp.every((item) => item === "");
            const trimmedIp = [...newIp];

            while (trimmedIp.length > 0 && trimmedIp[trimmedIp.length - 1] === "") {
                trimmedIp.pop();
            }

            const ipString = allEmpty ? null : trimmedIp.join(".");
            onChange(ipString);

            if (value.length === 3 && index < 3) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    }

    function handlePaste(e) {
        if (disabled) return;

        const pastedValue = e.clipboardData.getData("text").trim();
        const formatValue = pastedValue.split(".");

        if (formatValue.length !== 4 || !formatValue.every((item) => /^\d{1,3}$/.test(item))) {
            return;
        }

        e.preventDefault();
        setIp(formatValue);
        onChange(formatValue.join("."));
        inputRefs.current[3]?.focus();
    }

    function handleKeyDown(e, index) {
        if (e.key === "Backspace" && ip[index] === "" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${
                    error
                        ? theme.palette.primary.redPrimary
                        : focused
                            ? theme.palette.primary.greyPrimary
                            : theme.palette.primary.greyFifth
                }`,
                borderRadius: "8px",
                padding: "4px 8px",
                gap: "4px",
                margin: "5px",
                "&:hover": {
                    borderColor: error ? theme.palette.primary.redPrimary : theme.palette.primary.mainHover,
                },
            }}
            ref={boxRef}
            tabIndex={0}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
                if (!e.relatedTarget || !boxRef.current.contains(e.relatedTarget)) {
                    setFocused(false);
                }
            }}
        >
            {ip.map((value, index) => (
                <React.Fragment key={index}>
                    <TextField
                        sx={{
                            "& .MuiInputBase-root": {
                                textAlign: "center",
                            },
                            "& .MuiInputBase-input": {
                                textAlign: "center",
                                padding: 0,
                                color:  theme.palette.primary.greyPrimary
                            },
                            "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                                borderBottom: "none",
                            },
                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                borderBottom: "none",
                            },
                            backgroundColor: "transparent",
                        }}
                        inputProps={{
                            maxLength: 3,
                            style: { textAlign: "center" },
                        }}
                        value={value}
                        disabled={disabled}
                        inputRef={(ref) => (inputRefs.current[index] = ref)}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onPaste={handlePaste}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        variant="standard"
                        autoComplete='off'
                    />
                    {index < 3 && (
                        <Typography
                            sx={{
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.text.primary,
                            }}
                        >
                            .
                        </Typography>
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
}

export default IpInputField;
